import { Injectable, signal } from '@angular/core';
import { TourPackage } from '@appTypes/api.types';
import { tourFlightKey } from '@pages/tours-page/utils/tour-fares';

/** Which tour row to open again: the hotel offer of the search and the itinerary of the flight (never the fare). */
export interface TourReopenRequest {
  hotelNode: string;
  flightKey: string;
}

/**
 * Tour fare cards (docs features/flight-fare-picker/qa.md R-TB16): **Choose another fare** in "The price has changed" takes the tour out of the cart and goes
 * back to the Tours results; the row of the same hotel offer and the same itinerary opens its Details again, where the agent picks another fare.
 * The package id is `tour:{SearchId}:{HotelNodeId}:{FlightNodeId}` - the hotel node and the itinerary (flight numbers) tell the row; the flight node
 * differs per fare, so it is not part of the match. Root-provided: it carries one request across the page change, nothing else.
 */
@Injectable({ providedIn: 'root' })
export class TourFareReturnService {
  readonly #request = signal<TourReopenRequest | null>(null);
  /** Read by the Tours page to react to a request. */
  readonly request = this.#request.asReadonly();

  /** Remembers the tour whose Details should open on the results page. */
  requestReopen(pkg: Pick<TourPackage, 'id' | 'flight'> | null | undefined): void {
    const hotelNode = (pkg?.id ?? '').split(':')[2];
    const flightKey = tourFlightKey(pkg);
    this.#request.set(hotelNode && flightKey ? { hotelNode, flightKey } : null);
  }

  /** The row of the request among `rows`, once: the request is cleared when a row is found, so the Details open only once; null otherwise. */
  consume(rows: readonly TourPackage[] | null | undefined): TourPackage | null {
    const request = this.#request();
    if (!request || !rows?.length) return null;
    const row = rows.find((r) => r.id?.split(':')[2] === request.hotelNode && tourFlightKey(r) === request.flightKey);
    if (!row) return null;
    this.#request.set(null);
    return row;
  }
}
